import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { CONTACT_NUMBER } from "../mock";
import {
  MapPin, Star, ChevronLeft, ChevronRight, Wifi, Coffee, Waves, Dumbbell,
  ParkingCircle, Utensils, Sparkles, Bed, Users, Ruler, Check, X, Phone,
  Building2, ConciergeBell, Wine, ShowerHead, Wind, Tv, ShieldCheck
} from "lucide-react";

const amenityIcon = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("wi-fi") || n.includes("wifi")) return Wifi;
  if (n.includes("breakfast") || n.includes("restaurant") || n.includes("dining") || n.includes("bar")) return Utensils;
  if (n.includes("pool") || n.includes("beach") || n.includes("water")) return Waves;
  if (n.includes("gym") || n.includes("fitness")) return Dumbbell;
  if (n.includes("parking")) return ParkingCircle;
  if (n.includes("coffee") || n.includes("nespresso") || n.includes("tea")) return Coffee;
  if (n.includes("concierge") || n.includes("butler")) return ConciergeBell;
  if (n.includes("wine") || n.includes("lounge") || n.includes("minibar")) return Wine;
  if (n.includes("shower") || n.includes("bathtub")) return ShowerHead;
  if (n.includes("air") || n.includes("ac")) return Wind;
  if (n.includes("tv") || n.includes("smart")) return Tv;
  if (n.includes("safe") || n.includes("security")) return ShieldCheck;
  return Sparkles;
};

const HotelDetails = ({ hotel, open, onClose, onBook }) => {
  const [idx, setIdx] = useState(0);
  const gallery = hotel ? (hotel.images?.length ? hotel.images : (hotel.image ? [hotel.image] : [])) : [];

  useEffect(() => { setIdx(0); }, [hotel]);
  if (!hotel) return null;

  const go = (d) => setIdx((i) => (i + d + gallery.length) % gallery.length);

  // Build packages: prefer explicit room_packages; else derive from room_types
  const explicit = Array.isArray(hotel.room_packages) ? hotel.room_packages.filter((p) => p && p.room_type) : [];
  const derived = (hotel.room_types || []).map((rt) => ({
    room_type: rt, price: hotel.price_per_night, max_guests: rt.toLowerCase().includes("suite") ? 4 : 2,
    bed_type: rt.toLowerCase().includes("suite") ? "King" : "Queen", size_sqm: null, view: null,
    includes_breakfast: false, description: "", amenities: [],
  }));
  // Merge: use explicit; add any room_type not covered from derived list
  const packages = [...explicit];
  for (const d of derived) {
    if (!packages.find((p) => p.room_type === d.room_type)) packages.push(d);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0">
        {/* Gallery */}
        <div className="relative">
          <div className="aspect-[16/8] bg-neutral-100 overflow-hidden relative">
            {gallery.length > 0 ? (
              gallery.map((src, i) => (
                <img key={i} src={src} alt={`${hotel.name}-${i}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0"}`} />
              ))
            ) : (
              <div className="w-full h-full flex items-center justify-center text-neutral-400">No image</div>
            )}

            {gallery.length > 1 && (
              <>
                <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center shadow"><ChevronLeft size={18} /></button>
                <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center shadow"><ChevronRight size={18} /></button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1">
                  {gallery.map((_, i) => (
                    <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/70 hover:bg-white"}`} />
                  ))}
                </div>
                <div className="absolute top-3 right-3 bg-black/60 text-white text-[11px] uppercase tracking-widest px-2 py-1">
                  {idx + 1} / {gallery.length}
                </div>
              </>
            )}
          </div>

          {gallery.length > 1 && (
            <div className="px-6 py-3 border-t border-neutral-100 flex gap-2 overflow-x-auto">
              {gallery.map((src, i) => (
                <button key={i} onClick={() => setIdx(i)}
                  className={`w-24 h-16 flex-shrink-0 overflow-hidden border-2 transition-colors ${i === idx ? "border-[#c8102e]" : "border-transparent hover:border-neutral-300"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <button onClick={onClose} className="absolute top-3 left-3 w-9 h-9 bg-white/90 hover:bg-white flex items-center justify-center shadow"><X size={18} /></button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest">
                <span className="text-[#c8102e] font-medium">{hotel.brand}</span>
                {hotel.featured && <Badge className="bg-[#c8102e] text-white rounded-none"><Sparkles size={10} className="mr-1" /> Featured</Badge>}
                <span className="flex items-center gap-0.5 ml-1">
                  {Array.from({ length: hotel.star_rating || 0 }).map((_, i) => <Star key={i} size={12} className="fill-amber-400 text-amber-400" />)}
                </span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-medium mt-2 leading-tight">{hotel.name}</h2>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex items-start gap-2 text-neutral-800">
                  <MapPin size={14} className="text-[#c8102e] mt-0.5" />
                  <div>
                    <div>{[hotel.city, hotel.state, hotel.country].filter(Boolean).join(", ")}</div>
                    {hotel.address && <div className="text-xs text-neutral-500 mt-0.5">{hotel.address}</div>}
                  </div>
                </div>
                <a href={`tel:${CONTACT_NUMBER.replace(/\s/g, "")}`} className="flex items-center gap-2 text-neutral-800 hover:text-[#c8102e]">
                  <Phone size={14} className="text-[#c8102e]" /> {CONTACT_NUMBER}
                </a>
              </div>
            </div>
            <div className="md:text-right md:min-w-[220px]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Rooms from</div>
              <div className="text-3xl font-serif font-medium">{hotel.currency} {Number(hotel.price_per_night).toLocaleString("en-IN")}</div>
              <div className="text-xs text-neutral-500">per night · taxes extra</div>
              <Button onClick={() => onBook(hotel)} className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none mt-3 w-full md:w-auto">
                Book this hotel
              </Button>
            </div>
          </div>

          {hotel.description && (
            <div className="mt-6 border-t border-neutral-100 pt-6">
              <h3 className="text-xs font-medium uppercase tracking-widest text-neutral-500 mb-2">About this hotel</h3>
              <p className="text-sm text-neutral-800 leading-relaxed max-w-3xl">{hotel.description}</p>
            </div>
          )}

          {/* Facilities & Amenities */}
          {hotel.amenities?.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xs font-medium uppercase tracking-widest text-neutral-500 mb-4">Facilities & Amenities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {hotel.amenities.map((a) => {
                  const Icon = amenityIcon(a);
                  return (
                    <div key={a} className="flex items-center gap-2 border border-neutral-200 px-3 py-2.5 bg-white hover:border-[#c8102e] transition-colors">
                      <Icon size={15} className="text-[#c8102e] flex-shrink-0" />
                      <span className="text-sm text-neutral-800">{a}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Room Packages */}
          <div className="mt-10">
            <div className="flex items-end justify-between mb-4">
              <h3 className="text-xs font-medium uppercase tracking-widest text-neutral-500">Rooms & Packages</h3>
              <span className="text-xs text-neutral-500">{packages.length} package{packages.length > 1 ? "s" : ""} available</span>
            </div>
            {packages.length === 0 ? (
              <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 px-4 py-6 text-center">
                No room packages have been added yet.
              </div>
            ) : (
              <div className="space-y-3">
                {packages.map((p, i) => (
                  <div key={i} className="border border-neutral-200 hover:border-[#c8102e] transition-colors bg-white p-5 flex flex-col md:flex-row md:items-stretch gap-4">
                    <div className="md:w-16 flex md:flex-col items-center md:items-start md:justify-start gap-2 md:gap-1 md:border-r md:pr-4 md:border-neutral-100">
                      <Building2 size={20} className="text-[#c8102e]" />
                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">Room {i + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-serif text-xl font-medium">{p.room_type}</h4>
                        {p.includes_breakfast && (
                          <Badge variant="outline" className="rounded-none border-emerald-300 text-emerald-700 bg-emerald-50">
                            <Check size={11} className="mr-1" /> Breakfast included
                          </Badge>
                        )}
                      </div>
                      {p.description && <p className="text-sm text-neutral-700 mt-1.5 leading-relaxed">{p.description}</p>}
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-neutral-800">
                        <Fact icon={<Users size={13} />} label="Max guests" value={`${p.max_guests || 2}`} />
                        {p.bed_type && <Fact icon={<Bed size={13} />} label="Bed" value={p.bed_type} />}
                        {p.size_sqm && <Fact icon={<Ruler size={13} />} label="Size" value={`${p.size_sqm} sqm`} />}
                        {p.view && <Fact icon={<MapPin size={13} />} label="View" value={p.view} />}
                      </div>
                      {p.amenities?.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Room amenities</div>
                          <div className="flex flex-wrap gap-1">
                            {p.amenities.map((a) => (
                              <span key={a} className="text-[11px] border border-neutral-200 px-2 py-0.5 text-neutral-700">{a}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="md:w-56 md:border-l md:pl-4 md:border-neutral-100 flex md:flex-col justify-between md:justify-center md:text-right">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Per night</div>
                        <div className="text-2xl font-serif font-medium">{hotel.currency} {Number(p.price || hotel.price_per_night).toLocaleString("en-IN")}</div>
                        <div className="text-xs text-neutral-500">taxes extra</div>
                      </div>
                      <Button onClick={() => onBook(hotel, p.room_type)} className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none mt-3">
                        Book this room
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8 bg-[#c8102e]/5 border border-[#c8102e]/20 p-4 flex items-center gap-3">
            <Sparkles className="text-[#c8102e]" size={18} />
            <div className="text-sm text-neutral-800">
              Earn <span className="font-medium text-[#c8102e]">+10 AIP Credits</span> instantly on this booking — every unique booking ID counts.
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Fact = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <div className="text-[#c8102e] mt-0.5">{icon}</div>
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  </div>
);

export default HotelDetails;
